import React, { useState, useMemo, useEffect } from 'react';
import {
    Search,
    ArrowLeft,
    Users,
    Wallet,
    UserCheck,
    Download,
    Copy,
    CheckCircle,
    Building
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { bondsAPI, holdingsAPI, allocationRequestsAPI, formatCurrency, formatDate } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { createResponseSelector } from '../../utils/apiHelpers';
import { useAuth } from '../../contexts/AuthContext';

const formatId = (value) => {
    if (!value) {
        return '';
    }
    const stringValue = String(value);
    return stringValue.length > 10
        ? `${stringValue.slice(0, 6)}...${stringValue.slice(-4)}`
        : stringValue;
};

const PrimarySubscriberBondHolders = () => {
    const navigate = useNavigate();
    const { bondId } = useParams();
    const { user } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [holderTypeFilter, setHolderTypeFilter] = useState('all');
    const [copiedAddress, setCopiedAddress] = useState(null);

    // Get effective PS ID - use parent account ID for sub-accounts
    const effectivePsId = user?.parentAccountId || user?._id || user?.id;

    // Fetch bond data
    const { data: bondData, isPending: bondLoading } = useQuery({
        queryKey: ['bond', bondId],
        queryFn: () => bondsAPI.getBond(bondId),
        select: createResponseSelector('bond', {})
    });

    const bond = bondData || {};

    // Fetch holdings for this bond scoped to the current primary subscriber
    // This automatically includes parent account holdings for sub-accounts
    const { data: holdingsData = [], isPending: holdingsLoading } = useQuery({
        queryKey: ['bond-holdings', bondId, effectivePsId],
        enabled: !!bondId && !!effectivePsId,
        queryFn: () => holdingsAPI.getHoldingsByBond(bondId, { status: 'active' }),
        select: createResponseSelector(null, [])
    });

    const { data: bondCustodianRequestsData } = useQuery({
        queryKey: ['ps-bond-loc-requests', bondId, effectivePsId],
        enabled: Boolean(bondId && effectivePsId),
        queryFn: async () => {
            const response = await allocationRequestsAPI.getRequestsByBondAndPS(bondId);
            return response?.data?.data || response?.data || null;
        }
    });

    const bondHoldings = holdingsData || [];
    const custodianLookupList = bondCustodianRequestsData?.custodians || [];
    const psAccountId = effectivePsId ? String(effectivePsId) : null;

    const holdingsSummary = useMemo(() => {
        const summary = { totalAmount: 0, psAmount: 0 };
        if (!bondHoldings || bondHoldings.length === 0) {
            return summary;
        }

        bondHoldings.forEach((holding) => {
            const remainingAmount = holding?.remainingTokenAmount || 0;
            if (remainingAmount <= 0) {
                return;
            }
            summary.totalAmount += remainingAmount;

            const holderId = holding?.holderId?._id || holding?.holderId;
            if (psAccountId && holderId && String(holderId) === psAccountId) {
                summary.psAmount += remainingAmount;
            }
        });

        return summary;
    }, [bondHoldings, psAccountId]);

    const totalBondHoldingsAmount = holdingsSummary.totalAmount;
    const psDirectHoldingsAmount = holdingsSummary.psAmount;

    const custodianNameMap = useMemo(() => {
        const map = new Map();
        if (!bondHoldings || bondHoldings.length === 0) {
            return map;
        }

        bondHoldings.forEach((holding) => {
            const holderSnapshot = holding.holderSnapshot || holding.investorSnapshot || {};
            const investorSnapshot = holding.investorSnapshot || {};
            const holderId = holding.holderId?._id || holding.holderId || holding.investorId?._id || holding.investorId;
            if (!holderId) {
                return;
            }

            const isCustodian = holderSnapshot.isCustodian || investorSnapshot.isCustodian;
            if (!isCustodian) {
                return;
            }

            const displayName = holderSnapshot.name || investorSnapshot.name || holderSnapshot.email || investorSnapshot.email;
            if (displayName) {
                map.set(String(holderId), displayName);
            }
        });

        bondHoldings.forEach((holding) => {
            const metadataCustodianName = holding?.metadata?.custodianName;
            const custodianId = holding.custodianId?._id || holding.custodianId;
            if (!metadataCustodianName || !custodianId) {
                return;
            }
            if (!map.has(String(custodianId))) {
                map.set(String(custodianId), metadataCustodianName);
            }
        });

        custodianLookupList.forEach((custodian) => {
            if (!custodian) {
                return;
            }

            const idCandidates = [
                custodian.custodianId?._id || custodian.custodianId,
                custodian.userId?._id || custodian.userId
            ].filter(Boolean);

            if (idCandidates.length === 0) {
                return;
            }

            const displayName = custodian.name || custodian.email || custodian.custodianName || null;
            if (!displayName) {
                return;
            }

            idCandidates.forEach((idValue) => {
                map.set(String(idValue), displayName);
            });
        });

        return map;
    }, [bondHoldings, custodianLookupList]);

    // Build holders list from holdings data
    const holders = useMemo(() => {
        if (!bondHoldings || bondHoldings.length === 0) return [];

        const totalSupply = totalBondHoldingsAmount || 0;

        return bondHoldings
            .filter((holding) => (holding.remainingTokenAmount || 0) > 0)
            .map((holding, idx) => {
                const holderSnapshot = holding.holderSnapshot || holding.investorSnapshot || {};
                const investorSnapshot = holding.investorSnapshot || {};
                const remainingAmount = holding.remainingTokenAmount || 0;
                const originalAmount = holding.tokenAmount || remainingAmount;
                const holderId = holding.holderId?._id || holding.holderId || '';
                const investorId = holding.investorId?._id || holding.investorId || holderId;
                const custodianId = holding.custodianId?._id || holding.custodianId || null;
                const normalizedCustodianId = custodianId ? String(custodianId) : null;
                const isPrimarySubscriberHolder = Boolean(
                    holderSnapshot.isPrimarySubscriber === true ||
                    (psAccountId && holderId && String(holderId) === psAccountId && !normalizedCustodianId)
                );
                const isCustodianHolder = holderSnapshot.isCustodian || investorSnapshot.isCustodian;
                const nameCandidates = [
                    holderSnapshot.name,
                    holderSnapshot.email,
                    investorSnapshot.name,
                    investorSnapshot.email
                ];
                const holderName = nameCandidates.find(Boolean) || 'Unknown Holder';
                const walletAddress = holderSnapshot.walletAddress || investorSnapshot.walletAddress || null;
                const holderType = isPrimarySubscriberHolder
                    ? 'Primary Subscriber'
                    : (isCustodianHolder ? 'Custodian' : 'Investor');
                const pct = totalSupply > 0 ? (remainingAmount / totalSupply) * 100 : 0;
                const primarySubscriberName = holding.primarySubscriberSnapshot?.name || holding.primarySubscriberSnapshot?.email || null;
                const showPrimarySubscriberInfo = Boolean(primarySubscriberName && !isPrimarySubscriberHolder);
                const custodianName = normalizedCustodianId ? (custodianNameMap.get(normalizedCustodianId) || holding.metadata?.custodianName || null) : null;
                const showCustodianInfo = Boolean(normalizedCustodianId && !isPrimarySubscriberHolder && !isCustodianHolder);
                const displayName = showCustodianInfo
                    ? (custodianName || formatId(normalizedCustodianId) || holderName)
                    : holderName;
                const showCustodianBadge = Boolean(showCustodianInfo || isCustodianHolder);
                const showInvestorBadge = Boolean(!isPrimarySubscriberHolder && !showCustodianBadge);

                return {
                    id: holding._id || `H${idx}`,
                    holderId,
                    investorId,
                    primarySubscriberId: holding.primarySubscriberId?._id || holding.primarySubscriberId || null,
                    investorName: displayName,
                    holderName: displayName,
                    primarySubscriberName,
                    walletAddress,
                    holderType,
                    remainingAmount,
                    originalAmount,
                    holdingAmount: remainingAmount,
                    holdingPercentage: pct.toFixed(2),
                    registrationDate: holding.holdingsStartDate || holding.createdAt,
                    lastActivity: holding.updatedAt,
                    status: holding.holdingsEndDate ? 'Inactive' : 'Active',
                    kycStatus: 'Verified',
                    walletBalance: remainingAmount,
                    isWhitelisted: true,
                    email: holderSnapshot.email || investorSnapshot.email || null,
                    accountType: null,
                    approvalStatus: 'approved',
                    companyRegistrationNumber: null,
                    allocationRequestId: holding.allocationRequestId,
                    showPrimarySubscriberInfo,
                    custodianId: normalizedCustodianId,
                    custodianName,
                    showCustodianInfo,
                    showCustodianBadge,
                    showInvestorBadge,
                    isPrimarySubscriber: isPrimarySubscriberHolder,
                    showSubInvestorInfo: showCustodianInfo,
                    subInvestorName: showCustodianInfo ? holderName : null
                };
            }).sort((a, b) => b.remainingAmount - a.remainingAmount);
    }, [bondHoldings, totalBondHoldingsAmount, psAccountId, custodianNameMap]);

    // Filter holders
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredHolders = holders.filter(holder => {
        const searchFields = [
            holder.investorName,
            holder.holderId,
            holder.investorId,
            holder.walletAddress
        ].filter(Boolean).map(value => String(value).toLowerCase());

        const matchesSearch = normalizedSearch
            ? searchFields.some(field => field.includes(normalizedSearch))
            : true;

        const matchesType = holderTypeFilter === 'all' ||
            (holderTypeFilter === 'verified' && holder.kycStatus === 'Verified') ||
            (holderTypeFilter === 'pending' && holder.kycStatus === 'Pending') ||
            (holderTypeFilter === 'active' && holder.status === 'Active');

        return matchesSearch && matchesType;
    });

    // Calculate totals
    const totalSupplyFromHoldings = totalBondHoldingsAmount || 0;

    const copyToClipboard = (text, type) => {
        if (!text) {
            return;
        }
        navigator.clipboard.writeText(text);
        setCopiedAddress(type);
        setTimeout(() => setCopiedAddress(null), 2000);
    };

    if (bondLoading || holdingsLoading) {
        return <LoadingSpinner size="lg" text="Loading bond holders..." />;
    }

    if (!bond._id) {
        return (
            <div className="text-center py-12">
                <p className="text-error-600">Bond not found</p>
                <button onClick={() => navigate(-1)} className="mt-4 btn-primary">
                    Go Back
                </button>
            </div>
        );
    }

    if (!bondHoldings || bondHoldings.length === 0) {
        return (
            <div className="text-center py-12">
                <Building className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
                <p className="text-lg font-medium mb-2">No Holdings Found</p>
                <p className="text-secondary-600 mb-4">
                    No wallet currently holds tokens for this bond.
                </p>
                <button onClick={() => navigate(-1)} className="btn-primary">
                    Go Back
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Bond Token Holders</h1>
                            <p className="text-sm text-gray-500">
                                {bond.bondName} • {bond.isin || 'Pending ISIN'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => {/* TODO: Export functionality */ }}
                        className="btn-primary"
                    >
                        <Download className="w-4 h-4" />
                        <span>Export</span>
                    </button>
                </div>

                {/* Filters */}
                <div className="card">
                    <div className="card-body">
                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                            <div className="lg:col-span-3">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-gray w-4 h-4" />
                                    <input
                                        type="text"
                                        placeholder="Search by holder name, ID, or wallet address..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="form-input pl-10 w-full"
                                    />
                                </div>
                            </div>
                            <div className="lg:col-span-2">
                                <select
                                    value={holderTypeFilter}
                                    onChange={(e) => setHolderTypeFilter(e.target.value)}
                                    className="form-input w-full"
                                >
                                    <option value="all">All Holders</option>
                                    <option value="verified">Verified Only</option>
                                    <option value="pending">Pending Verification</option>
                                    <option value="active">Active Only</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Holders Table */}
                <div className="card">
                    <div className="card-header">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-medium text-secondary-900">
                                Token Holders ({filteredHolders.length})
                            </h2>
                            <span className="text-sm text-secondary-600">
                                Total Held: {formatCurrency(totalBondHoldingsAmount)}
                            </span>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead className="table-header">
                                <tr>
                                    <th className="table-header-cell">Holder</th>
                                    <th className="table-header-cell">Holdings</th>
                                    <th className="table-header-cell">Wallet</th>
                                </tr>
                            </thead>
                            <tbody className="table-body">
                                {filteredHolders.length > 0 ? (
                                    filteredHolders.map((holder) => (
                                        <tr key={holder.id} className="table-row">
                                            <td className="table-cell align-top">
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <div className="font-medium text-secondary-900">
                                                            {holder.investorName}
                                                        </div>
                                                        {holder.isPrimarySubscriber && (
                                                            <span className="text-[10px] tracking-wide uppercase px-2 py-0.5 rounded border bg-blue-50 text-blue-700 border-blue-100">
                                                                Primary Subscriber
                                                            </span>
                                                        )}
                                                        {holder.showCustodianBadge && (
                                                            <span className="text-[10px] tracking-wide uppercase px-2 py-0.5 rounded border bg-green-50 text-green-700 border-green-100">
                                                                Custodian
                                                            </span>
                                                        )}
                                                        {holder.showInvestorBadge && (
                                                            <span className="text-[10px] tracking-wide uppercase px-2 py-0.5 rounded border bg-purple-50 text-purple-700 border-purple-100">
                                                                Investor
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-dark-gray space-y-0.5">
                                                        {holder.holderId && (
                                                            <div><span className="text-dark-gray">Holder ID:</span> <code className="text-[10px] bg-secondary-50 px-1 rounded">{holder.holderId}</code></div>
                                                        )}
                                                        {holder.showCustodianInfo && (
                                                            <div><span className="text-dark-gray">Custodian:</span> {holder.custodianName || formatId(holder.custodianId)}</div>
                                                        )}
                                                        {holder.showSubInvestorInfo && holder.subInvestorName && (
                                                            <div><span className="text-dark-gray">Sub-Investor:</span> {holder.subInvestorName}</div>
                                                        )}
                                                        {holder.showPrimarySubscriberInfo && holder.primarySubscriberName && (
                                                            <div><span className="text-dark-gray">Primary Subscriber:</span> {holder.primarySubscriberName}</div>
                                                        )}
                                                        {holder.email && (
                                                            <div><span className="text-dark-gray">Email:</span> {holder.email}</div>
                                                        )}
                                                        {holder.accountType && (
                                                            <div><span className="text-dark-gray">Account:</span> {holder.accountType}</div>
                                                        )}
                                                        {holder.approvalStatus && (
                                                            <div><span className="text-dark-gray">Approval:</span> {holder.approvalStatus}</div>
                                                        )}
                                                        {holder.registrationDate && (
                                                            <div><span className="text-dark-gray">Created:</span> {formatDate(holder.registrationDate)}</div>
                                                        )}
                                                        {holder.companyRegistrationNumber && (
                                                            <div><span className="text-dark-gray">Reg #:</span> {holder.companyRegistrationNumber}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="table-cell">
                                                <div>
                                                    <div className="font-medium text-secondary-900">
                                                        {formatCurrency(holder.remainingAmount)}
                                                    </div>
                                                    <div className="text-xs text-secondary-500">
                                                        Remaining amount
                                                    </div>
                                                    {holder.originalAmount !== holder.remainingAmount && (
                                                        <div className="text-xs text-dark-gray mt-1">
                                                            Original: {formatCurrency(holder.originalAmount)}
                                                        </div>
                                                    )}
                                                    <div className="text-sm text-secondary-500 mt-1">
                                                        {holder.holdingPercentage}% of PS allocation
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="table-cell">
                                                {holder.walletAddress ? (
                                                    <div className="flex items-center space-x-2">
                                                        <code className="text-xs bg-secondary-100 px-2 py-1 rounded font-mono">
                                                            {holder.walletAddress.length > 10
                                                                ? `${holder.walletAddress.slice(0, 6)}...${holder.walletAddress.slice(-4)}`
                                                                : holder.walletAddress}
                                                        </code>
                                                        {holder.accountType && (
                                                            <span className="text-[10px] tracking-wide uppercase bg-primary-50 text-primary-700 px-2 py-0.5 rounded border border-primary-100">
                                                                {holder.accountType}
                                                            </span>
                                                        )}
                                                        <button
                                                            onClick={() => copyToClipboard(holder.walletAddress, holder.id)}
                                                            className="text-secondary-600 hover:text-secondary-700"
                                                        >
                                                            {copiedAddress === holder.id ? (
                                                                <CheckCircle className="w-4 h-4 text-success-600" />
                                                            ) : (
                                                                <Copy className="w-4 h-4" />
                                                            )}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="text-xs text-secondary-500">
                                                        Wallet pending linkage
                                                    </div>
                                                )}
                                                <div className="text-xs text-dark-gray mt-1">
                                                    Balance: {formatCurrency(holder.walletBalance)}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="3" className="table-cell text-center py-12">
                                            <div className="text-secondary-500">
                                                <Users className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
                                                <p className="text-lg font-medium mb-2">No token holders found</p>
                                                <p className="text-sm">
                                                    {searchTerm
                                                        ? `No holders match your search "${searchTerm}"`
                                                        : 'No holders have been allocated tokens for this bond yet'
                                                    }
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrimarySubscriberBondHolders;
